import type { GridPoint } from "../types/grid";
import type { LevelData } from "../types/level";
import { createLevelBounds, isPointWithinBounds } from "./Grid";
import type { LevelState } from "./LevelState";

export interface FogComputation {
  readonly visible: readonly GridPoint[];
  readonly explored: readonly GridPoint[];
  readonly permanentReveal: boolean;
  readonly debugReveal: boolean;
}

function createPointKey(point: GridPoint): string {
  return `${point.x},${point.y}`;
}

function createAllGridPoints(level: LevelData): readonly GridPoint[] {
  const points: GridPoint[] = [];

  for (let y = 0; y < level.grid.height; y += 1) {
    for (let x = 0; x < level.grid.width; x += 1) {
      points.push({ x, y });
    }
  }

  return points;
}

function collectRadiusVisibility(level: LevelData, center: GridPoint, radius: number): readonly GridPoint[] {
  const bounds = createLevelBounds(level.grid);
  const points: GridPoint[] = [];

  for (let y = center.y - radius; y <= center.y + radius; y += 1) {
    for (let x = center.x - radius; x <= center.x + radius; x += 1) {
      const point = { x, y };
      if (isPointWithinBounds(point, bounds)) {
        points.push(point);
      }
    }
  }

  return points;
}

function collectRectVisibility(level: LevelData): readonly GridPoint[] {
  const bounds = createLevelBounds(level.grid);
  const visited = new Set<string>();
  const points: GridPoint[] = [];

  for (const rect of level.initialVisibility.rects) {
    for (let y = rect.y; y < rect.y + rect.height; y += 1) {
      for (let x = rect.x; x < rect.x + rect.width; x += 1) {
        const point = { x, y };
        if (!isPointWithinBounds(point, bounds)) {
          continue;
        }

        const key = createPointKey(point);
        if (visited.has(key)) {
          continue;
        }

        visited.add(key);
        points.push(point);
      }
    }
  }

  return points;
}

function mergeExplored(previous: readonly GridPoint[], nextVisible: readonly GridPoint[]): readonly GridPoint[] {
  const explored: GridPoint[] = [];
  const visited = new Set<string>();

  for (const point of [...previous, ...nextVisible]) {
    const key = createPointKey(point);
    if (visited.has(key)) {
      continue;
    }

    visited.add(key);
    explored.push({ x: point.x, y: point.y });
  }

  return explored;
}

export function computeFog(state: LevelState, level: LevelData): FogComputation {
  const allGridPoints = createAllGridPoints(level);

  if (state.fog.permanentReveal) {
    return {
      visible: [...allGridPoints],
      explored: [...allGridPoints],
      permanentReveal: true,
      debugReveal: state.fog.debugReveal,
    };
  }

  const baseVisible = level.initialVisibility.mode === "radius"
    ? collectRadiusVisibility(level, state.player, level.initialVisibility.radius)
    : collectRectVisibility(level);

  return {
    visible: state.fog.debugReveal ? [...allGridPoints] : baseVisible,
    explored: mergeExplored(state.fog.explored, baseVisible),
    permanentReveal: false,
    debugReveal: state.fog.debugReveal,
  };
}
